import { useEffect, useState, useRef } from "react";
import { Plus, Pencil, Trash2, Upload, Package, X, Image } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { getShopProductsAdmin, createShopProduct, updateShopProduct, deleteShopProduct, uploadFile } from "@/lib/api";

const initialFormData = { 
  name: "", 
  description: "", 
  price: "", 
  cost: "",
  image_url: "", 
  image_urls: [],
  category: "merchandise", 
  in_stock: true, 
  order: 0 
};

export default function AdminShop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingExtra, setUploadingExtra] = useState(false);
  const fileInputRef = useRef(null);
  const extraFileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Alleen afbeeldingen zijn toegestaan");
      return;
    }

    setUploading(true);
    try {
      const res = await uploadFile(file);
      setFormData({...formData, image_url: res.data.url});
      toast.success("Hoofdafbeelding geüpload");
    } catch (error) {
      toast.error("Kon afbeelding niet uploaden");
    } finally {
      setUploading(false);
    }
  };

  const handleExtraFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const invalidFiles = files.filter(f => !f.type.startsWith('image/'));
    if (invalidFiles.length > 0) {
      toast.error("Alleen afbeeldingen zijn toegestaan");
      return;
    }

    setUploadingExtra(true);
    try {
      const uploadPromises = files.map(file => uploadFile(file));
      const results = await Promise.all(uploadPromises);
      const newUrls = results.map(res => res.data.url);
      setFormData({...formData, image_urls: [...formData.image_urls, ...newUrls]});
      toast.success(`${newUrls.length} extra afbeelding(en) geüpload`);
    } catch (error) {
      toast.error("Kon afbeeldingen niet uploaden");
    } finally {
      setUploadingExtra(false);
    }
  };

  const removeExtraImage = (index) => {
    const newUrls = formData.image_urls.filter((_, i) => i !== index);
    setFormData({...formData, image_urls: newUrls});
  };

  const fetchProducts = async () => {
    try {
      const response = await getShopProductsAdmin();
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSwitchChange = (checked) => {
    setFormData({ ...formData, in_stock: checked });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const data = { 
      ...formData, 
      price: parseFloat(formData.price),
      cost: parseFloat(formData.cost) || 0,
      order: parseInt(formData.order) 
    };

    try {
      if (editingId) {
        await updateShopProduct(editingId, data);
        toast.success("Product bijgewerkt");
      } else {
        await createShopProduct(data);
        toast.success("Product aangemaakt");
      }
      setDialogOpen(false);
      setFormData(initialFormData);
      setEditingId(null);
      fetchProducts();
    } catch (error) {
      toast.error("Kon product niet opslaan");
      console.error("Error saving product:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (product) => {
    setFormData({
      name: product.name,
      description: product.description || "",
      price: product.price.toString(),
      cost: (product.cost || 0).toString(),
      image_url: product.image_url || "",
      image_urls: product.image_urls || [],
      category: product.category,
      in_stock: product.in_stock,
      order: product.order.toString(),
    });
    setEditingId(product.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Weet je zeker dat je dit product wilt verwijderen?")) return;

    try {
      await deleteShopProduct(id);
      toast.success("Product verwijderd");
      fetchProducts();
    } catch (error) {
      toast.error("Kon product niet verwijderen");
      console.error("Error deleting product:", error);
    }
  };

  const openNewDialog = () => {
    setFormData(initialFormData);
    setEditingId(null);
    setDialogOpen(true);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(price);
  };

  const totalImages = (product) => {
    let count = product.image_url ? 1 : 0;
    count += (product.image_urls || []).length;
    return count;
  };

  return (
    <div className="p-6" data-testid="admin-shop-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Package className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Chivo']">Shopproducten</h1>
            <p className="text-slate-600">Beheer merchandise en producten</p>
          </div>
        </div>
        <Button onClick={openNewDialog} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-product">
          <Plus className="h-4 w-4 mr-2" />
          Product Toevoegen
        </Button>
      </div>

      <Card className="bg-white border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 border-slate-200">
                <TableHead className="text-slate-600">Afbeelding</TableHead>
                <TableHead className="text-slate-600">Naam</TableHead>
                <TableHead className="text-slate-600">Prijs</TableHead>
                <TableHead className="text-slate-600">Kostprijs</TableHead>
                <TableHead className="text-slate-600">Categorie</TableHead>
                <TableHead className="text-slate-600">Voorraad</TableHead>
                <TableHead className="text-right text-slate-600">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-600">Laden...</TableCell>
                </TableRow>
              ) : products.length > 0 ? (
                products.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>
                      <div className="relative">
                        {product.image_url ? (
                          <img src={product.image_url} alt={product.name} className="h-12 w-12 object-cover rounded" />
                        ) : (
                          <div className="h-12 w-12 bg-slate-200 rounded flex items-center justify-center text-slate-400">
                            Geen afb
                          </div>
                        )}
                        {totalImages(product) > 1 && (
                          <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-blue-600 text-[10px]">
                            {totalImages(product)}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium text-slate-900">{product.name}</p>
                      <p className="text-sm text-slate-500 truncate max-w-xs">{product.description}</p>
                    </TableCell>
                    <TableCell className="font-medium text-slate-900">{formatPrice(product.price)}</TableCell>
                    <TableCell className="text-slate-600">{formatPrice(product.cost || 0)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-slate-300 text-slate-600">{product.category}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={product.in_stock ? "bg-green-500/20 text-green-600" : "bg-red-500/20 text-red-600"}>
                        {product.in_stock ? "Op Voorraad" : "Uitverkocht"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleEdit(product)} className="border-slate-200 text-slate-600 hover:text-slate-900">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDelete(product.id)} className="border-slate-200 text-red-600 hover:text-red-700 hover:bg-red-50">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-600">Geen producten gevonden. Voeg je eerste product toe!</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-white border-slate-200 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-900">{editingId ? "Product Bewerken" : "Product Toevoegen"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-700">Naam</Label>
              <Input id="name" name="name" value={formData.name} onChange={handleChange} required className="border-slate-300" data-testid="product-name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description" className="text-slate-700">Beschrijving</Label>
              <Textarea id="description" name="description" value={formData.description} onChange={handleChange} required rows={3} className="border-slate-300" data-testid="product-description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price" className="text-slate-700">Verkoopprijs (EUR)</Label>
                <Input id="price" name="price" type="number" step="0.01" min="0" value={formData.price} onChange={handleChange} required className="border-slate-300" data-testid="product-price" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cost" className="text-slate-700">Kostprijs (EUR)</Label>
                <Input id="cost" name="cost" type="number" step="0.01" min="0" value={formData.cost} onChange={handleChange} className="border-slate-300" placeholder="0.00" data-testid="product-cost" />
                <p className="text-xs text-slate-500">Voor winstberekening (niet zichtbaar voor klanten)</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="category" className="text-slate-700">Categorie</Label>
              <Input id="category" name="category" value={formData.category} onChange={handleChange} className="border-slate-300" placeholder="bijv. merchandise" data-testid="product-category" />
            </div>
            
            {/* Main Image */}
            <div className="space-y-2">
              <Label className="text-slate-700">Hoofdafbeelding</Label>
              <div className="flex gap-2">
                <Input 
                  name="image_url" 
                  value={formData.image_url} 
                  onChange={handleChange} 
                  placeholder="URL of upload een afbeelding" 
                  className="border-slate-300 flex-1" 
                  data-testid="product-image-url"
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  ref={fileInputRef}
                />
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="border-slate-300"
                  data-testid="upload-main-image"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading ? "..." : "Upload"}
                </Button>
              </div>
              {formData.image_url && (
                <div className="relative w-full h-32 mt-2">
                  <img src={formData.image_url} alt="Preview" className="w-full h-full object-cover rounded-lg" />
                  <Button
                    type="button"
                    size="icon"
                    variant="destructive"
                    className="absolute top-1 right-1 h-6 w-6"
                    onClick={() => setFormData({...formData, image_url: ""})}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>

            {/* Extra Images */}
            <div className="space-y-2">
              <Label className="text-slate-700">Extra Afbeeldingen</Label>
              <div className="flex gap-2">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleExtraFileUpload}
                  className="hidden"
                  ref={extraFileInputRef}
                />
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => extraFileInputRef.current?.click()}
                  disabled={uploadingExtra}
                  className="border-slate-300 w-full"
                  data-testid="upload-extra-images"
                >
                  <Image className="h-4 w-4 mr-2" />
                  {uploadingExtra ? "Uploaden..." : "Extra Afbeeldingen Toevoegen"}
                </Button>
              </div>
              {formData.image_urls.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {formData.image_urls.map((url, index) => (
                    <div key={index} className="relative aspect-square">
                      <img src={url} alt={`Extra ${index + 1}`} className="w-full h-full object-cover rounded-lg" />
                      <Button
                        type="button"
                        size="icon"
                        variant="destructive"
                        className="absolute top-1 right-1 h-5 w-5"
                        onClick={() => removeExtraImage(index)}
                        data-testid={`remove-extra-image-${index}`}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-500">Deze afbeeldingen worden getoond in een slider op de productpagina</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="order" className="text-slate-700">Weergavevolgorde</Label>
              <Input id="order" name="order" type="number" min="0" value={formData.order} onChange={handleChange} className="border-slate-300" data-testid="product-order" />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="in_stock" className="text-slate-700">Op Voorraad</Label>
              <Switch id="in_stock" checked={formData.in_stock} onCheckedChange={handleSwitchChange} data-testid="product-in-stock" />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-300 text-slate-700">Annuleren</Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-product">{saving ? "Opslaan..." : "Opslaan"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
